import { useState, useEffect } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useOrganization, useUser, SignedIn } from "@clerk/clerk-react";
import {
  SubscriptionDetailsButton,
  CheckoutButton,
  usePlans,
} from "@clerk/clerk-react/experimental";
import { useQuery } from "@tanstack/react-query";
import { currentOrgQuery } from "@/lib/queries";
import { useDisconnectGitHub } from "@/lib/mutations";
import { apiFetch } from "@/lib/api";
import {
  IconBrandGithub,
  IconBrandSlack,
  IconBrandGoogle,
  IconCheck,
  IconAlertCircle,
  IconCreditCard,
  IconArrowsExchange,
  IconMail,
  IconTrash,
  IconLink,
} from "@tabler/icons-react";
import { Button } from "@/ui/button";
import { Badge } from "@/ui/badge";
import { Spinner } from "@/ui/spinner";
import { Input } from "@/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/ui/select";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/ui/accordion";
import { toast } from "sonner";

export function SettingsPage() {
  const { user } = useUser();
  const { organization, memberships, invitations } = useOrganization({
    memberships: { pageSize: 50 },
    invitations: { pageSize: 50 },
  });
  const { data: orgData, isLoading } = useQuery(currentOrgQuery());
  const { data: plans } = usePlans({ for: "organization" });
  const search = useSearch({ from: "/dashboard/settings" });
  const navigate = useNavigate();
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<"org:member" | "org:admin">(
    "org:member"
  );
  const [isInviting, setIsInviting] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  const hasGitHub = orgData?.organization?.hasGitHubConnected;
  const paidPlans = plans?.filter((plan) => plan.hasBaseFee) || [];

  // GitHub disconnect mutation
  const disconnectGitHub = useDisconnectGitHub(orgData?.organization?.id || "");
  const membersList = memberships?.data || [];
  const pendingInvitations =
    invitations?.data?.filter((inv) => inv.status === "pending") || [];

  useEffect(() => {
    if (search.github === "success") {
      toast.success("GitHub account connected successfully!");
      navigate({
        from: "/dashboard/settings",
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          github: undefined,
          error: undefined,
        }),
      });
    } else if (search.error) {
      toast.error(`Connection failed: ${search.error}`);
      navigate({
        from: "/dashboard/settings",
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          github: undefined,
          error: undefined,
        }),
      });
    }
  }, [search.github, search.error, navigate]);

  const handleConnectGitHub = async () => {
    if (!orgData?.organization?.id) {
      toast.error("Organization data not loaded. Please try again.");
      return;
    }
    try {
      const { url } = await apiFetch<{ url: string }>(
        `/organizations/${orgData.organization.id}/github/connect`
      );
      window.location.href = url;
    } catch (error) {
      console.error("GitHub connect error:", error);
      toast.error("Failed to initiate connection");
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail || !organization) return;

    setIsInviting(true);
    try {
      await organization.inviteMember({
        emailAddress: inviteEmail,
        role: inviteRole,
      });
      toast.success(`Invitation sent to ${inviteEmail}`);
      setInviteEmail("");
      void invitations?.revalidate?.();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to send invitation";
      toast.error(message);
    } finally {
      setIsInviting(false);
    }
  };

  const handleRemoveMember = async (userId: string, memberName: string) => {
    const membership = memberships?.data?.find(
      (m) => m.publicUserData?.userId === userId
    );
    if (!membership) return;

    try {
      await membership.destroy();
      toast.success(`${memberName} has been removed`);
      void memberships?.revalidate?.();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to remove member";
      toast.error(message);
    }
  };

  const handleChangeRole = async (userId: string, newRole: string) => {
    const membership = memberships?.data?.find(
      (m) => m.publicUserData?.userId === userId
    );
    if (!membership) return;

    try {
      await membership.update({ role: newRole });
      toast.success("Role updated successfully");
      void memberships?.revalidate?.();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to update role";
      toast.error(message);
    }
  };

  const handleRevokeInvitation = async (
    invitationId: string,
    email: string
  ) => {
    const invitation = invitations?.data?.find((i) => i.id === invitationId);
    if (!invitation) return;

    try {
      await invitation.revoke();
      toast.success(`Invitation to ${email} revoked`);
      void invitations?.revalidate?.();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to revoke invitation";
      toast.error(message);
    }
  };

  const handleDeleteAccount = async () => {
    if (!user) return;

    setIsDeletingAccount(true);
    try {
      await user.delete();
      toast.success("Account deleted successfully");
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed to delete account";
      toast.error(message);
      setIsDeletingAccount(false);
    }
  };

  const getRoleLabel = (role: string) => {
    if (role === "org:admin") return "Admin";
    return "Member";
  };

  const getInitials = (
    firstName?: string | null,
    lastName?: string | null,
    identifier?: string | null
  ) => {
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    }
    if (firstName) return firstName[0].toUpperCase();
    if (identifier) return identifier[0].toUpperCase();
    return "?";
  };

  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case "google":
        return <IconBrandGoogle className="w-2.5 h-2.5" />;
      case "github":
        return <IconBrandGithub className="w-2.5 h-2.5" />;
      default:
        return <IconLink className="w-2.5 h-2.5" />;
    }
  };

  if (!organization || !user) return null;

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">
          Manage connections and account
        </p>
      </div>

      {/* Main Accordion */}
      <Accordion>
        {/* Connections */}
        <AccordionItem value="connections">
          <AccordionTrigger>
            <div className="text-left flex-1">
              <span className="font-medium">Connections</span>
              <div className="text-muted-foreground text-xs">
                {hasGitHub ? "1 connected" : "0 connected"}
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-1 pt-3">
              {/* GitHub */}
              <div className="flex items-center gap-2 py-1 px-1 rounded hover:bg-muted/30">
                <div className="w-5 h-5 rounded bg-[#24292f] flex items-center justify-center shrink-0">
                  <IconBrandGithub className="w-3 h-3 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-xs font-medium">GitHub</span>
                    {isLoading ? (
                      <Badge
                        variant="secondary"
                        className="gap-0.5 text-[9px] px-1 py-0"
                      >
                        <Spinner className="size-2" />
                        Checking
                      </Badge>
                    ) : hasGitHub ? (
                      <Badge
                        variant="success"
                        className="gap-0.5 text-[9px] px-1 py-0"
                      >
                        <IconCheck className="w-2.5 h-2.5" />
                        Connected
                      </Badge>
                    ) : (
                      <Badge
                        variant="warning"
                        className="gap-0.5 text-[9px] px-1 py-0"
                      >
                        <IconAlertCircle className="w-2.5 h-2.5" />
                        Not connected
                      </Badge>
                    )}
                  </div>
                  <p className="text-[10px] text-muted-foreground">
                    Sync repositories and create pull requests
                  </p>
                </div>
                {hasGitHub ? (
                  <AlertDialog>
                    <AlertDialogTrigger
                      render={
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 text-[10px] px-2"
                          disabled={disconnectGitHub.isPending}
                        >
                          {disconnectGitHub.isPending ? (
                            <Spinner className="w-3 h-3 mr-1" />
                          ) : null}
                          Disconnect
                        </Button>
                      }
                    />
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Disconnect GitHub</AlertDialogTitle>
                        <AlertDialogDescription>
                          Are you sure you want to disconnect GitHub? You will
                          need to reconnect to create pull requests and sync
                          repositories.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => {
                            disconnectGitHub.mutate(undefined, {
                              onSuccess: () => {
                                toast.success("GitHub disconnected");
                              },
                              onError: (err) => {
                                toast.error(
                                  "Failed to disconnect: " + err.message
                                );
                              },
                            });
                          }}
                          className="bg-destructive hover:bg-destructive/90"
                        >
                          Disconnect
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                ) : (
                  <Button
                    size="sm"
                    className="h-6 text-[10px] px-2"
                    onClick={handleConnectGitHub}
                  >
                    <IconBrandGithub className="w-3 h-3 mr-1" />
                    Connect
                  </Button>
                )}
              </div>

              {/* Slack */}
              <div className="flex items-center gap-2 py-1 px-1 rounded hover:bg-muted/30">
                <div className="w-5 h-5 rounded bg-[#4A154B] flex items-center justify-center shrink-0">
                  <IconBrandSlack className="w-3 h-3 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium">Slack</span>
                  <p className="text-[10px] text-muted-foreground">
                    Get notifications in Slack
                  </p>
                </div>
                <Button
                  size="sm"
                  className="h-6 text-[10px] px-2"
                  onClick={() => toast.info("Slack integration coming soon")}
                >
                  <IconBrandSlack className="w-3 h-3 mr-1" />
                  Connect
                </Button>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Billing */}
        <AccordionItem value="billing">
          <AccordionTrigger>
            <div className="text-left flex-1">
              <span className="font-medium">Billing</span>
              <div className="text-muted-foreground text-xs">
                Manage subscription and payment
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="pt-3">
              <div className="flex items-center gap-2 py-1 px-1 rounded hover:bg-muted/30">
                <div className="w-5 h-5 rounded bg-primary/10 flex items-center justify-center shrink-0">
                  <IconCreditCard className="w-3 h-3 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium">
                    Billing & Subscription
                  </span>
                  <p className="text-[10px] text-muted-foreground">
                    Manage subscription, payment method, or cancel
                  </p>
                </div>
                <SignedIn>
                  <div className="flex items-center gap-1.5">
                    <DropdownMenu>
                      <DropdownMenuTrigger
                        render={
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-6 text-[10px] px-2"
                          >
                            <IconArrowsExchange className="w-3 h-3 mr-1" />
                            Change Plan
                          </Button>
                        }
                      />
                      <DropdownMenuContent align="end" className="w-40">
                        {paidPlans.map((plan) => (
                          <CheckoutButton
                            key={plan.id}
                            planId={plan.id}
                            planPeriod="month"
                            for="organization"
                            onSubscriptionComplete={() =>
                              toast.success(`Switched to ${plan.name}`)
                            }
                          >
                            <DropdownMenuItem>
                              <div className="flex flex-col">
                                <span className="text-xs font-medium">
                                  {plan.name}
                                </span>
                                <span className="text-[10px] text-muted-foreground">
                                  {plan.fee?.currencySymbol}
                                  {plan.fee?.amountFormatted}/mo
                                </span>
                              </div>
                            </DropdownMenuItem>
                          </CheckoutButton>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                    <SubscriptionDetailsButton
                      for="organization"
                      onSubscriptionCancel={() =>
                        toast.success("Subscription canceled")
                      }
                    >
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 text-[10px] px-2"
                      >
                        Manage
                      </Button>
                    </SubscriptionDetailsButton>
                  </div>
                </SignedIn>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Team */}
        <AccordionItem value="team">
          <AccordionTrigger>
            <div className="text-left flex-1">
              <span className="font-medium">Team</span>
              <div className="text-muted-foreground text-xs">
                {memberships?.count || 0} member
                {(memberships?.count || 0) !== 1 ? "s" : ""}
                {pendingInvitations.length > 0 &&
                  `, ${pendingInvitations.length} pending`}
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2 pt-3">
              {/* Invite */}
              <div className="flex items-center gap-1.5 px-1">
                <Input
                  type="email"
                  placeholder="Enter email to invite"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="h-7 text-xs flex-1"
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && inviteEmail) {
                      handleInviteMember();
                    }
                  }}
                />
                <Select
                  value={inviteRole}
                  onValueChange={(value) => {
                    if (value === "org:member" || value === "org:admin")
                      setInviteRole(value);
                  }}
                >
                  <SelectTrigger className="w-20 h-7 text-[10px]">
                    {getRoleLabel(inviteRole)}
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="org:member">Member</SelectItem>
                    <SelectItem value="org:admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  className="h-7 text-[10px] px-3"
                  onClick={handleInviteMember}
                  disabled={!inviteEmail || isInviting}
                >
                  {isInviting ? <Spinner className="w-3 h-3 mr-1" /> : null}
                  Invite
                </Button>
              </div>

              {/* Members List */}
              {memberships?.isLoading ? (
                <div className="flex items-center justify-center py-2">
                  <Spinner className="size-3" />
                </div>
              ) : (
                <div className="space-y-0.5">
                  {membersList.map((member) => {
                    const isCurrentUser =
                      member.publicUserData?.userId === user?.id;
                    const memberName =
                      `${member.publicUserData?.firstName || ""} ${
                        member.publicUserData?.lastName || ""
                      }`.trim() ||
                      member.publicUserData?.identifier ||
                      "Unknown";

                    return (
                      <div
                        key={member.id}
                        className="flex items-center gap-2 py-1 px-1 rounded hover:bg-muted/30"
                      >
                        <Avatar size="sm" className="w-5 h-5">
                          <AvatarImage src={member.publicUserData?.imageUrl} />
                          <AvatarFallback className="text-[8px] bg-primary/10 text-primary">
                            {getInitials(
                              member.publicUserData?.firstName,
                              member.publicUserData?.lastName,
                              member.publicUserData?.identifier
                            )}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1">
                            <span className="text-xs truncate">
                              {memberName}
                            </span>
                            {isCurrentUser && (
                              <Badge
                                variant="secondary"
                                className="text-[8px] px-1 py-0"
                              >
                                You
                              </Badge>
                            )}
                          </div>
                          <p className="text-[10px] text-muted-foreground truncate">
                            {member.publicUserData?.identifier}
                          </p>
                        </div>
                        <Select
                          value={member.role}
                          onValueChange={(value) => {
                            if (value)
                              handleChangeRole(
                                member.publicUserData?.userId || "",
                                value
                              );
                          }}
                          disabled={isCurrentUser}
                        >
                          <SelectTrigger className="w-16 h-5 text-[10px]">
                            {getRoleLabel(member.role)}
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="org:admin">Admin</SelectItem>
                            <SelectItem value="org:member">Member</SelectItem>
                          </SelectContent>
                        </Select>
                        {!isCurrentUser && (
                          <AlertDialog>
                            <AlertDialogTrigger
                              render={
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-5 w-5 p-0 text-muted-foreground hover:text-destructive"
                                >
                                  <IconTrash className="w-3 h-3" />
                                </Button>
                              }
                            />
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>
                                  Remove member
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                  Are you sure you want to remove {memberName}{" "}
                                  from the organization? This action cannot be
                                  undone.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() =>
                                    handleRemoveMember(
                                      member.publicUserData?.userId || "",
                                      memberName
                                    )
                                  }
                                  className="bg-destructive hover:bg-destructive/90"
                                >
                                  Remove
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pending Invitations */}
              {pendingInvitations.length > 0 && (
                <div className="space-y-0.5 border-t border-border/50 pt-2">
                  <div className="text-[10px] text-muted-foreground px-1 mb-1">
                    Pending invitations
                  </div>
                  {pendingInvitations.map((invitation) => (
                    <div
                      key={invitation.id}
                      className="flex items-center gap-2 py-1 px-1 rounded hover:bg-muted/30"
                    >
                      <Avatar size="sm" className="w-5 h-5">
                        <AvatarFallback className="text-[8px] bg-amber-500/10 text-amber-500">
                          {invitation.emailAddress[0].toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs truncate">
                            {invitation.emailAddress}
                          </span>
                          <span className="text-[10px] text-muted-foreground">
                            · {getRoleLabel(invitation.role)}
                          </span>
                        </div>
                      </div>
                      <AlertDialog>
                        <AlertDialogTrigger
                          render={
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-5 text-[10px] text-muted-foreground hover:text-destructive"
                            >
                              Revoke
                            </Button>
                          }
                        />
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              Revoke invitation
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to revoke the invitation to{" "}
                              {invitation.emailAddress}?
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() =>
                                handleRevokeInvitation(
                                  invitation.id,
                                  invitation.emailAddress
                                )
                              }
                              className="bg-destructive hover:bg-destructive/90"
                            >
                              Revoke
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Account */}
        <AccordionItem value="account">
          <AccordionTrigger>
            <div className="text-left flex-1">
              <span className="font-medium">Account</span>
              <div className="text-muted-foreground text-xs">
                {user.fullName || user.primaryEmailAddress?.emailAddress}
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="space-y-2 pt-3">
              {/* Profile */}
              <div className="flex items-center gap-2 px-1">
                <Avatar size="sm" className="w-6 h-6">
                  <AvatarImage src={user.imageUrl} />
                  <AvatarFallback className="text-[9px] bg-primary/10 text-primary">
                    {getInitials(
                      user.firstName,
                      user.lastName,
                      user.primaryEmailAddress?.emailAddress
                    )}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium">
                    {user.fullName || "No name set"}
                  </span>
                  <p className="text-[10px] text-muted-foreground">
                    {user.primaryEmailAddress?.emailAddress}
                  </p>
                </div>
              </div>

              {/* Email Addresses */}
              <div className="space-y-0.5 border-t border-border/50 pt-2">
                <div className="text-[10px] text-muted-foreground px-1 mb-1">
                  Email addresses
                </div>
                {user.emailAddresses.map((email) => (
                  <div
                    key={email.id}
                    className="flex items-center gap-2 py-0.5 px-1"
                  >
                    <IconMail className="w-3 h-3 text-muted-foreground" />
                    <span className="text-xs flex-1">{email.emailAddress}</span>
                    {email.id === user.primaryEmailAddressId && (
                      <Badge
                        variant="secondary"
                        className="text-[8px] px-1 py-0"
                      >
                        Primary
                      </Badge>
                    )}
                  </div>
                ))}
              </div>

              {/* Connected Accounts */}
              <div className="space-y-0.5 border-t border-border/50 pt-2">
                <div className="text-[10px] text-muted-foreground px-1 mb-1">
                  Connected accounts
                </div>
                {user.externalAccounts.length > 0 ? (
                  user.externalAccounts.map((account) => (
                    <div
                      key={account.id}
                      className="flex items-center gap-2 py-0.5 px-1"
                    >
                      <div className="w-4 h-4 rounded bg-muted flex items-center justify-center shrink-0">
                        {getProviderIcon(account.provider)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs capitalize">
                          {account.provider}
                        </span>
                        <p className="text-[10px] text-muted-foreground truncate">
                          {account.emailAddress}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="py-1 px-1 text-xs text-muted-foreground">
                    No connected accounts
                  </div>
                )}
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Danger Zone */}
        <AccordionItem
          value="danger"
          className="border-l-2 border-l-destructive"
        >
          <AccordionTrigger>
            <div className="text-left flex-1">
              <span className="font-medium text-destructive">Danger Zone</span>
              <div className="text-muted-foreground text-xs">
                Delete account permanently
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="pt-3">
              <div className="flex items-center gap-2 py-1 px-1 rounded hover:bg-destructive/5">
                <div className="w-5 h-5 rounded bg-destructive/10 flex items-center justify-center shrink-0">
                  <IconTrash className="w-3 h-3 text-destructive" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium">Delete account</span>
                  <p className="text-[10px] text-muted-foreground">
                    Permanently delete your account and all data
                  </p>
                </div>
                <AlertDialog>
                  <AlertDialogTrigger
                    render={
                      <Button
                        variant="destructive"
                        size="sm"
                        className="h-6 text-[10px] px-2"
                        disabled={isDeletingAccount}
                      >
                        {isDeletingAccount ? (
                          <Spinner className="w-3 h-3 mr-1" />
                        ) : null}
                        Delete
                      </Button>
                    }
                  />
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete account</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete your account? This
                        action cannot be undone. All your data will be
                        permanently removed.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteAccount}
                        className="bg-destructive hover:bg-destructive/90"
                      >
                        Delete account
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
}
